import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import moment from "moment";
import swal from "sweetalert";
import TabMedioPago from "./TabMedioPago";
import {
  actualizarOrdenPago,
  getBancosCuentasActivos,
  getOrdenPagoById,
  previewRetenciones,
} from "./ApiServices";
import {
  ajustarImportesAdelantos,
  armarMedioPagoAgregadoDesdeConfigs,
  armarMedioPagoDesdeConfigProveedor,
  buildEstadoWizardDesdeDetalle,
  buildGenerarOrdenPagoBody,
  buildPreviewAdelantosLocal,
  cantidadChequesMedio,
  configPagoDesdeMedioPago,
  configPagoProveedorVacia,
  configsPagoSeleccionCompletas,
  editarRetencionPreviewProveedor,
  enriquecerCuentasOrigen,
  esDocumentoAdelanto,
  esMedioChequeProv,
  idDocumentoPendiente,
  importeEsValido,
  mapDetalleOrdenPagoResponse,
  mapFilaAComprobanteBody,
  mapImputacionAFilaComprobante,
  medioPagoInicial,
  MENSAJE_ERROR_IMPORTES,
  mergeAdelantosEnPreview,
  mergePreviewsProveedores,
  normalizarMensajeErrorPreview,
  obtenerComprobantesPreview,
  obtenerResumenPreview,
  previewTieneResumen,
  requiereMediosPago,
  retencionesBodyDesdeGrupo,
  validarMedioPagoMulti,
} from "./utils";

/**
 * Editor unificado del lote: misma UI que el paso 3 del wizard
 * (comprobantes, retenciones, propuesta por proveedor y medios).
 */
export default function EditorLoteOrdenPago({
  proveedoresLote = [],
  editable = true,
  onGuardado,
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [documentos, setDocumentos] = useState([]);
  const [importesAPagar, setImportesAPagar] = useState({});
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [medioPago, setMedioPago] = useState(null);
  const [configPagoPorProveedor, setConfigPagoPorProveedor] = useState({});
  const [cuentasBancarias, setCuentasBancarias] = useState([]);
  const [loadingCuentas, setLoadingCuentas] = useState(false);
  const [guardando, setGuardando] = useState(false);
  /** Meta por CUIT: id OP, nro, email, razón social. */
  const [opsPorCuit, setOpsPorCuit] = useState({});

  const proveedoresGrupos = useMemo(() => {
    const map = new Map();
    documentos.forEach((doc) => {
      const cuit = String(doc.nro_cuit || "").replace(/\D/g, "");
      if (!cuit) return;
      if (!map.has(cuit)) {
        map.set(cuit, {
          cuit,
          razon_social: doc.razon_social || opsPorCuit[cuit]?.razon_social || "",
          cbu: doc.cbu || "",
          id_banco: doc.id_banco ?? null,
          banco_nombre: doc.banco_nombre || "",
          documentos: [],
        });
      }
      map.get(cuit).documentos.push(doc);
    });
    return Array.from(map.values());
  }, [documentos, opsPorCuit]);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const editables = (proveedoresLote || []).filter((prov) => {
        const opId = prov?.ordenPago?.id ?? prov?.ordenPago?.id_orden_pago;
        return Boolean(opId);
      });

      if (!editables.length) {
        setError("No hay órdenes de pago editables en este lote.");
        setLoading(false);
        return;
      }

      const detalles = await Promise.all(
        editables.map(async (prov) => {
          const opId = prov.ordenPago.id ?? prov.ordenPago.id_orden_pago;
          const data = await getOrdenPagoById(opId);
          const detalle = mapDetalleOrdenPagoResponse(data);
          const estadoWizard = buildEstadoWizardDesdeDetalle(detalle, data);
          const cuit =
            String(
              estadoWizard.cuitConsultado ||
                prov.cuit ||
                ""
            ).replace(/\D/g, "") || "";
          const docs = (
            estadoWizard.resultado?.proveedores?.[0]?.documentos ??
            estadoWizard.resultado?.documentos ??
            []
          ).map((doc) => ({
            ...doc,
            nro_cuit: cuit,
            razon_social:
              doc.razon_social ||
              estadoWizard.resultado?.proveedores?.[0]?.razon_social ||
              prov.razon_social ||
              "",
          }));
          return {
            cuit,
            opId: Number(opId),
            nroOp: estadoWizard.nroOpEdicion ?? null,
            email: estadoWizard.resultado?.email || "",
            razon_social:
              estadoWizard.resultado?.proveedores?.[0]?.razon_social ||
              prov.razon_social ||
              "",
            docs,
            importes: estadoWizard.importesAPagar ?? {},
            medioPago: estadoWizard.medioPago ?? medioPagoInicial(moment()),
            preview: estadoWizard.preview ?? null,
          };
        })
      );

      const todosDocs = [];
      const importes = {};
      const meta = {};
      const configs = {};
      let chequesRecibidos = [];
      let chequesEmitidos = [];
      let fechaPago = null;
      let observaciones = "";

      detalles.forEach((item) => {
        meta[item.cuit] = {
          id: item.opId,
          nro_op: item.nroOp,
          email: item.email,
          razon_social: item.razon_social,
        };
        configs[item.cuit] =
          configPagoDesdeMedioPago(item.medioPago) || configPagoProveedorVacia();
        Object.assign(importes, item.importes);
        todosDocs.push(...item.docs);
        if (!fechaPago && item.medioPago?.fechaPago) {
          fechaPago = item.medioPago.fechaPago;
        }
        if (!observaciones && item.medioPago?.observaciones) {
          observaciones = item.medioPago.observaciones;
        }
        chequesRecibidos = [
          ...chequesRecibidos,
          ...(item.medioPago?.cheque?.recibidos ?? []).map((cheque) => ({
            ...cheque,
            cuit_asignado:
              String(cheque?.cuit_asignado || "").replace(/\D/g, "") ||
              item.cuit,
          })),
        ];
        chequesEmitidos = [
          ...chequesEmitidos,
          ...(item.medioPago?.cheque?.emitidos ?? []).map((cheque) => ({
            ...cheque,
            cuit_asignado:
              String(cheque?.cuit_asignado || "").replace(/\D/g, "") ||
              item.cuit,
          })),
        ];
      });

      const proveedoresConTotalInicial = detalles.map((item) => ({
        cuit: item.cuit,
        razon_social: item.razon_social,
        total:
          Number(item.preview?.resumen?.total_a_pagar ?? item.preview?.resumen_global?.total_a_pagar) ||
          0,
      }));

      const medioBase = {
        ...medioPagoInicial(fechaPago || moment()),
        fechaPago: fechaPago || moment(),
        observaciones: observaciones || "",
        cheque: {
          activo: chequesRecibidos.length > 0 || chequesEmitidos.length > 0,
          recibidos: chequesRecibidos,
          emitidos: chequesEmitidos,
        },
      };

      const medioAgregado = armarMedioPagoAgregadoDesdeConfigs({
        configPorCuit: configs,
        proveedoresConTotal: proveedoresConTotalInicial,
        medioPagoBase: medioBase,
        cuentasBancarias: [],
      });

      setDocumentos(todosDocs);
      setImportesAPagar(importes);
      setOpsPorCuit(meta);
      setConfigPagoPorProveedor(configs);
      setMedioPago(medioAgregado);
      setPreview(
        mergePreviewsProveedores(
          detalles.map((item) => ({
            cuit: item.cuit,
            razon_social: item.razon_social,
            preview: item.preview,
          }))
        )
      );
    } catch (err) {
      setError(err.message || "No se pudo cargar el lote para editar");
    } finally {
      setLoading(false);
    }
  }, [proveedoresLote]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  useEffect(() => {
    if (!editable) return;
    setLoadingCuentas(true);
    getBancosCuentasActivos()
      .then(setCuentasBancarias)
      .catch(() => setCuentasBancarias([]))
      .finally(() => setLoadingCuentas(false));
  }, [editable]);

  const handleImportesAPagarChange = useCallback(
    (updater) => {
      setImportesAPagar((prev) => {
        const draft =
          typeof updater === "function" ? updater(prev) : { ...updater };
        const { importes } = ajustarImportesAdelantos(documentos, draft);
        const prevKeys = Object.keys(prev);
        const nextKeys = Object.keys(importes);
        if (
          prevKeys.length === nextKeys.length &&
          nextKeys.every((k) => String(prev[k]) === String(importes[k]))
        ) {
          return prev;
        }
        return importes;
      });
    },
    [documentos]
  );

  const cuitDeDoc = useCallback((doc) => {
    return String(doc?.nro_cuit || doc?.cuit || "").replace(/\D/g, "");
  }, []);

  const handleQuitarComprobante = useCallback(
    (idDoc) => {
      const id = String(idDoc || "");
      if (!id) return;
      setDocumentos((prev) => {
        if (prev.length <= 1) return prev;
        const target = prev.find(
          (doc) => String(idDocumentoPendiente(doc)) === id
        );
        if (!target) return prev;
        const cuit = cuitDeDoc(target);
        const delMismo = prev.filter((doc) => cuitDeDoc(doc) === cuit);
        if (delMismo.length <= 1) return prev;
        return prev.filter((doc) => String(idDocumentoPendiente(doc)) !== id);
      });
      setImportesAPagar((prev) => {
        if (prev[id] == null) return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      });
    },
    [cuitDeDoc]
  );

  const puedeQuitarComprobante = useCallback(
    (row) => {
      const cuit = cuitDeDoc(row);
      if (!cuit) return documentos.length > 1;
      return documentos.filter((doc) => cuitDeDoc(doc) === cuit).length > 1;
    },
    [documentos, cuitDeDoc]
  );

  const errorImportes = useMemo(() => {
    if (!documentos.length) return "";
    const invalido = documentos.some((row) => {
      const id = idDocumentoPendiente(row);
      const importe = importesAPagar[id] ?? row.saldo_pendiente;
      return !importeEsValido(importe, row.saldo_pendiente, row);
    });
    return invalido ? MENSAJE_ERROR_IMPORTES : "";
  }, [documentos, importesAPagar]);

  useEffect(() => {
    if (!editable || loading || !proveedoresGrupos.length) return undefined;
    let activo = true;

    if (errorImportes) {
      setPreview(null);
      setPreviewError(errorImportes);
      setPreviewLoading(false);
      return () => {
        activo = false;
      };
    }

    setPreviewLoading(true);
    const timer = window.setTimeout(async () => {
      if (!activo) return;
      setPreviewError("");
      try {
        const items = await Promise.all(
          proveedoresGrupos.map(async (grupo) => {
            const facturas = grupo.documentos.filter(
              (row) => !esDocumentoAdelanto(row)
            );
            const adelantos = grupo.documentos.filter(esDocumentoAdelanto);
            const idOp = opsPorCuit[String(grupo.cuit)]?.id;

            let previewProv;
            if (facturas.length > 0) {
              const comprobantes = facturas.map((row) =>
                mapFilaAComprobanteBody(row, importesAPagar)
              );
              previewProv = await previewRetenciones(grupo.cuit, {
                comprobantes,
                ...(idOp ? { id_orden_pago_edicion: Number(idOp) } : {}),
              });
              previewProv = mergeAdelantosEnPreview(
                previewProv,
                adelantos,
                importesAPagar
              );
            } else {
              previewProv = buildPreviewAdelantosLocal(
                adelantos,
                importesAPagar
              );
            }

            return {
              cuit: grupo.cuit,
              razon_social: grupo.razon_social,
              preview: previewProv,
            };
          })
        );
        if (!activo) return;
        setPreview(mergePreviewsProveedores(items));
      } catch (err) {
        if (!activo) return;
        setPreview(null);
        setPreviewError(
          normalizarMensajeErrorPreview(
            err.message || "Error al calcular retenciones"
          )
        );
      } finally {
        if (activo) setPreviewLoading(false);
      }
    }, 400);

    return () => {
      activo = false;
      window.clearTimeout(timer);
    };
  }, [
    editable,
    loading,
    proveedoresGrupos,
    importesAPagar,
    errorImportes,
    opsPorCuit,
  ]);

  const totalAPagarRaw = obtenerResumenPreview(preview)?.total_a_pagar;
  const totalAPagar =
    totalAPagarRaw != null && totalAPagarRaw !== ""
      ? Number(totalAPagarRaw)
      : null;

  const proveedoresConTotal = useMemo(
    () =>
      proveedoresGrupos.map((grupo) => {
        const previewGrupo =
          preview?.grupos?.find((g) => String(g.cuit) === String(grupo.cuit)) ??
          null;
        const total =
          Number(
            previewGrupo?.resumen?.total_a_pagar ??
              (proveedoresGrupos.length === 1 ? totalAPagar : 0)
          ) || 0;
        return {
          cuit: grupo.cuit,
          razon_social: grupo.razon_social || "",
          cbu: grupo.cbu || "",
          id_banco: grupo.id_banco ?? null,
          banco_nombre: grupo.banco_nombre || "",
          total,
        };
      }),
    [proveedoresGrupos, preview, totalAPagar]
  );

  useEffect(() => {
    if (!editable || loading) return;
    if (!proveedoresConTotal.length) return;
    if (!Object.keys(configPagoPorProveedor || {}).length) return;

    setMedioPago((prev) =>
      armarMedioPagoAgregadoDesdeConfigs({
        configPorCuit: configPagoPorProveedor,
        proveedoresConTotal,
        medioPagoBase: prev || medioPagoInicial(moment()),
        cuentasBancarias,
      })
    );
  }, [
    editable,
    loading,
    proveedoresConTotal,
    configPagoPorProveedor,
    cuentasBancarias,
  ]);

  const comprobantesTabla = useMemo(() => {
    const idsActivos = new Set(
      documentos
        .map((d) => idDocumentoPendiente(d))
        .filter(Boolean)
        .map(String)
    );
    const delPreview = obtenerComprobantesPreview(preview);
    const mapDetalle = new Map(
      documentos.map((c) => [String(idDocumentoPendiente(c)), c])
    );
    const filas =
      delPreview.length > 0
        ? delPreview.map((c) => {
            const fila = mapImputacionAFilaComprobante(c);
            const id = String(
              idDocumentoPendiente(fila) || c.idcomprobante || ""
            );
            return {
              ...fila,
              ...mapDetalle.get(id),
              nro_cuit: c.nro_cuit ?? mapDetalle.get(id)?.nro_cuit,
              razon_social: c.razon_social ?? mapDetalle.get(id)?.razon_social,
            };
          })
        : documentos.map((c) => ({
            ...c,
            importe_a_pagar:
              importesAPagar[idDocumentoPendiente(c)] ??
              c.importe_a_pagar ??
              c.saldo_pendiente,
          }));
    if (idsActivos.size > 0) {
      return filas.filter((c) => {
        const id = idDocumentoPendiente(c);
        return id && idsActivos.has(String(id));
      });
    }
    return filas;
  }, [preview, documentos, importesAPagar]);

  const handleMedioPagoChange = useCallback((partial) => {
    setMedioPago((prev) => {
      const next = { ...(prev || {}) };
      Object.keys(partial || {}).forEach((clave) => {
        const valor = partial[clave];
        const esObjetoPlano =
          valor != null &&
          typeof valor === "object" &&
          !Array.isArray(valor) &&
          !(valor instanceof Date) &&
          !moment.isMoment(valor);
        if (esObjetoPlano) {
          next[clave] = { ...(prev?.[clave] || {}), ...valor };
        } else {
          next[clave] = valor;
        }
      });
      return next;
    });
  }, []);

  const handleConfigPagoProveedorChange = useCallback((cuit, next) => {
    setConfigPagoPorProveedor((prev) => ({
      ...prev,
      [String(cuit)]: {
        ...(prev?.[String(cuit)] || configPagoProveedorVacia()),
        ...next,
      },
    }));
  }, []);

  const handleEditarRetencion = useCallback((cuit, clave, cambios) => {
    setPreview((prev) =>
      editarRetencionPreviewProveedor(prev, cuit, clave, cambios)
    );
  }, []);

  const configsPagoOk = useMemo(
    () =>
      configsPagoSeleccionCompletas(
        proveedoresGrupos.map((g) => g.cuit),
        configPagoPorProveedor,
        Object.fromEntries(
          proveedoresGrupos.map((g) => [String(g.cuit), g])
        )
      ),
    [proveedoresGrupos, configPagoPorProveedor]
  );

  const medioPagoValido = useMemo(() => {
    if (!medioPago) return false;
    if (totalAPagar != null && !requiereMediosPago(totalAPagar)) return true;
    if (!configsPagoOk) return false;
    return Boolean(
      validarMedioPagoMulti(medioPago, totalAPagar, proveedoresConTotal)
    );
  }, [medioPago, totalAPagar, proveedoresConTotal, configsPagoOk]);

  const handleGuardar = async () => {
    if (!editable || guardando) return;
    if (
      errorImportes ||
      !previewTieneResumen(preview) ||
      !medioPagoValido ||
      !configsPagoOk
    ) {
      await swal({
        title: "No se puede guardar",
        text:
          errorImportes ||
          previewError ||
          "Revisá importes, retenciones y medios de pago de cada proveedor.",
        icon: "warning",
        closeOnClickOutside: false,
      });
      return;
    }

    setGuardando(true);
    try {
      for (const grupo of proveedoresGrupos) {
        const meta = opsPorCuit[String(grupo.cuit)];
        if (!meta?.id) continue;

        const previewGrupo =
          preview?.grupos?.find((g) => String(g.cuit) === String(grupo.cuit)) ??
          null;
        const totalProv =
          Number(
            previewGrupo?.resumen?.total_a_pagar ??
              (proveedoresGrupos.length === 1 ? totalAPagar : 0)
          ) || 0;

        const cfg = configPagoPorProveedor[String(grupo.cuit)];
        const medioPagoEscalado = cfg?.medio
          ? armarMedioPagoDesdeConfigProveedor({
              config: cfg,
              totalAPagar: totalProv,
              medioPagoBase: medioPago,
              cuentasBancarias,
              cuit: grupo.cuit,
            })
          : medioPago;

        if (
          esMedioChequeProv(cfg?.medio) &&
          cantidadChequesMedio(medioPagoEscalado, grupo.cuit) === 0
        ) {
          throw new Error(
            `Completá los cheques para ${grupo.razon_social || grupo.cuit}`
          );
        }

        const comprobantes = grupo.documentos.map((row) =>
          mapFilaAComprobanteBody(row, importesAPagar)
        );
        const cuentasProv = enriquecerCuentasOrigen(
          medioPagoEscalado.transferencia?.cuentasOrigen ?? [],
          cuentasBancarias
        );

        const body = buildGenerarOrdenPagoBody({
          comprobantes,
          fechaPago: moment(medioPago.fechaPago).format("YYYY-MM-DD"),
          observaciones: medioPago.observaciones,
          medioPago: medioPagoEscalado,
          cuentasOrigenDetalle: cuentasProv,
          user: localStorage.getItem("user"),
          email: meta.email || "",
          totalAPagar: totalProv,
          retenciones: retencionesBodyDesdeGrupo(previewGrupo, preview),
        });

        await actualizarOrdenPago(meta.id, body);
      }

      await swal({
        title: "Lote actualizado",
        text: `Se guardaron los cambios de ${proveedoresGrupos.length} orden${
          proveedoresGrupos.length === 1 ? "" : "es"
        } de pago.`,
        icon: "success",
        closeOnClickOutside: false,
      });
      if (typeof onGuardado === "function") onGuardado();
      else await cargar();
    } catch (err) {
      swal({
        title: "No se pudo guardar",
        text: err.message || "Error al actualizar las órdenes del lote",
        icon: "error",
        closeOnClickOutside: false,
      });
    } finally {
      setGuardando(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={cargar}>
            Reintentar
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  if (!medioPago) {
    return <Alert severity="warning">No hay datos de medio de pago.</Alert>;
  }

  return (
    <Box>
      {editable ? (
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          spacing={1}
          sx={{ mb: 2 }}
        >
          <Typography variant="body2" color="text.secondary">
            Edición como en el paso 3: importes, retenciones, propuesta por
            proveedor y medios de pago.
          </Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={
              guardando || previewLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <SaveOutlinedIcon />
              )
            }
            disabled={
              guardando ||
              previewLoading ||
              Boolean(errorImportes) ||
              Boolean(previewError) ||
              !medioPagoValido ||
              !previewTieneResumen(preview)
            }
            onClick={handleGuardar}
          >
            {guardando
              ? "Guardando…"
              : previewLoading
                ? "Calculando…"
                : "Guardar cambios"}
          </Button>
        </Stack>
      ) : null}

      <Box
        sx={{
          opacity: previewLoading ? 0.7 : 1,
          transition: "opacity 0.15s ease",
        }}
      >
        <TabMedioPago
          comprobantesTabla={comprobantesTabla}
          preview={preview}
          previewLoading={previewLoading}
          previewError={previewError}
          medioPago={medioPago}
          onMedioPagoChange={editable ? handleMedioPagoChange : () => {}}
          cuentasBancarias={cuentasBancarias}
          loadingCuentas={loadingCuentas}
          cuitProveedor={
            proveedoresGrupos.length === 1 ? proveedoresGrupos[0].cuit : ""
          }
          compensacionHabilitada={proveedoresGrupos.length === 1}
          proveedores={proveedoresConTotal}
          configPagoPorProveedor={configPagoPorProveedor}
          onConfigPagoChange={
            editable ? handleConfigPagoProveedorChange : undefined
          }
          mostrarComprobantes
          comprobantesEditables={editable}
          importesAPagar={importesAPagar}
          onImportesAPagarChange={
            editable ? handleImportesAPagarChange : undefined
          }
          onQuitarComprobante={editable ? handleQuitarComprobante : undefined}
          puedeQuitarComprobante={
            editable ? puedeQuitarComprobante : undefined
          }
          cantidadSeleccionada={documentos.length}
          filasSeleccionadas={documentos}
          onEditarRetencion={editable ? handleEditarRetencion : undefined}
        />
      </Box>
    </Box>
  );
}
